import { Injectable } from '@angular/core';
import { HttpResponse } from '@angular/common/http';

/**
 * Session cache backing `ConfigCacheInterceptor` for the office/user config-reference endpoints
 * named "not done" in docs/backend/duplicate-api-calls-frontend-response.md — everything from
 * §3 of docs/backend/FRONTEND-DUPLICATE-API-CALLS-2026-09-17.md except tag-settings/me/offices-my,
 * which already have their own NgRx-backed freshness window via `StateCacheFetchService`.
 *
 * Kept as its own injectable, rather than folded into the interceptor, so write paths elsewhere
 * (project create/rename, staff changes, task-status admin, preferences save, library uploads)
 * can evict the entries they invalidate without depending on the interceptor itself.
 */
@Injectable({ providedIn: 'root' })
export class ConfigCacheService {
  private readonly cache = new Map<string, HttpResponse<any>>();

  get(key: string): HttpResponse<any> | undefined {
    return this.cache.get(key);
  }

  set(key: string, response: HttpResponse<any>): void {
    this.cache.set(key, response);
  }

  /**
   * Drops every cached entry whose URL contains `fragment` — used when a write makes a whole
   * endpoint family stale regardless of its exact query string (office id, page, search, …).
   */
  evict(fragment: string): void {
    for (const key of this.cache.keys()) {
      if (key.includes(fragment)) {
        this.cache.delete(key);
      }
    }
  }

  /** Office switch / logout: none of this session's cached config data belongs to what comes next. */
  clear(): void {
    this.cache.clear();
  }
}
